import { useEffect, useState } from 'react';
import './App.css';
import { SnackbarProvider } from 'notistack';
import RoutesComponent from './Routes-Nav/RoutesComponent';
import PersistentDrawer from './Routes-Nav/AppDrawer/PersistentDrawer';
import { BrowserRouter } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline, GlobalStyles } from '@mui/material';
import UserContext from './User/UserSettings/UserContext';
import API from './api2';

function App() {
  const userStr = localStorage.getItem('user');
  const userTokenStr = localStorage.getItem('userToken');
  const initialUser = userStr ? JSON.parse(userStr) : { mode: 'dark', token: null };
  if (initialUser && initialUser.token === null && userTokenStr) {
    initialUser.token = userTokenStr;
  }
  const [user, setUser] = useState(initialUser);

  // Background enabled state (moved up so theme can use it)
  const getInitialBg = () => {
    const v = localStorage.getItem('backgroundEnabled');
    if (v === null) return true;
    return v === 'true';
  };
  const [backgroundEnabled, setBackgroundEnabled] = useState<boolean>(getInitialBg());

  // Adjustable glass effect settings
  // Tweak these two constants (or promote to user settings) to adjust transparency
  const glassOpacityLight = 0.65; // when light mode
  const glassOpacityDark = 0.35;  // when dark mode (no background image)
  const glassOpacityDarkWithBg = 0.55; // darker cards over busy background image in dark mode
  const glassBlurPx = 8;          // blur radius for backdrop filter
  const glassBorder = true;       // quick toggle for subtle border

  useEffect(() => {
    if (user?.token) {
      API.token = user.token;
    }
  }, [user]);

  useEffect(() => {
    if (user && user.token) {
      localStorage.setItem('user', JSON.stringify(user));
    }
  }, [user]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'backgroundEnabled') {
        setBackgroundEnabled(e.newValue === 'true');
      }
    };
    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent)?.detail;
      if (!detail) return;
      if (detail.key === 'backgroundEnabled') {
        setBackgroundEnabled(detail.value === 'true');
      }
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('backgroundToggle', onCustom as EventListener);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('backgroundToggle', onCustom as EventListener);
    };
  }, []);

  const isDark = user.mode === 'dark';

  // Compute glass colors once
  const glassBg = (alpha: number) => isDark
    ? `rgba(25,26,28,${alpha})` // slightly deeper than before for better contrast
    : `rgba(255,255,255,${alpha})`;

  // Final opacity used
  const targetOpacity = isDark
    ? (backgroundEnabled ? glassOpacityDarkWithBg : glassOpacityDark)
    : glassOpacityLight;

  const theme = createTheme({
    palette: {
      mode: user.mode || 'dark',
      primary: {
        main: isDark ? '#90caf9' : '#1976d2',
      },
      secondary: {
        main: isDark ? '#5e81ac' : '#2c5282',
      },
      background: {
        default: isDark ? '#1d1e20' : '#f5f5f5',
        paper: backgroundEnabled
          ? glassBg(targetOpacity) // semi-transparent instead of fully transparent
          : (isDark ? '#252628' : '#ffffff'),
      },
      success: { main: '#4caf50' },
      error: { main: '#f44336' },
      text: {
        primary: isDark ? '#ffffff' : '#1d1e20',
        secondary: isDark ? '#b0b0b0' : '#707070',
      },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: backgroundEnabled ? {
            backgroundColor: glassBg(targetOpacity),
            backdropFilter: `blur(${glassBlurPx}px)`,
            WebkitBackdropFilter: `blur(${glassBlurPx}px)`,
            boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
            border: glassBorder ? (isDark ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(0,0,0,0.08)') : 'none',
            transition: 'background-color 250ms ease, box-shadow 250ms ease',
            // Hover refinement
            '&:hover': {
              backgroundColor: glassBg(Math.min(1, targetOpacity + 0.05)),
              boxShadow: '0 6px 30px rgba(0,0,0,0.40)'
            }
          } : {}
        }
      },
      MuiPaper: {
        variants: [
          {
            props: { variant: 'solid' },
            style: {
              backgroundColor: isDark ? '#252628' : '#ffffff',
              backdropFilter: 'none',
              WebkitBackdropFilter: 'none',
              boxShadow: isDark
                ? '0 2px 18px rgba(0,0,0,0.55)'
                : '0 2px 12px rgba(0,0,0,0.18)',
              border: isDark
                ? '1px solid rgba(255,255,255,0.06)'
                : '1px solid rgba(0,0,0,0.06)'
            }
          }
        ],
        styleOverrides: {
          root: ({ ownerState }) => (backgroundEnabled && ownerState.variant !== 'solid'
            ? {
              backgroundImage: 'none',
              backgroundColor: glassBg(targetOpacity),
              backdropFilter: `blur(${glassBlurPx}px)`,
              WebkitBackdropFilter: `blur(${glassBlurPx}px)`,
              border: glassBorder ? (isDark ? '1px solid rgba(255,255,255,0.09)' : '1px solid rgba(0,0,0,0.07)') : 'none',
              boxShadow: '0 2px 14px rgba(0,0,0,0.32)'
            }
            : {})
        }
      }
    }
  });

  return (<>
    <SnackbarProvider maxSnack={3}>
      <ThemeProvider theme={theme}>
        <UserContext.Provider value={{ user, setUser }}>
          <CssBaseline />
          {backgroundEnabled && (
            <GlobalStyles
              styles={(theme) => ({
                'html, body, #root': { height: '100%' },
                body: {
                  margin: 0,
                  position: 'relative',
                  backgroundColor: theme.palette.background.default,
                },
                'body::before': {
                  content: '""',
                  position: 'fixed',
                  inset: 0,
                  zIndex: -2,
                  backgroundImage: 'url("/images/potatoinhand.jpg")',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundAttachment: 'fixed',
                },
              })}
            />
          )}
          <BrowserRouter>
            {user?.token ? <PersistentDrawer>
              <RoutesComponent />
            </PersistentDrawer> : <RoutesComponent />}
          </BrowserRouter>
        </UserContext.Provider>
      </ThemeProvider>
    </SnackbarProvider>
  </>);
}

export default App;
